/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { BadgeProps } from './types';
import { forwardRef } from 'react';
import { useDefinition } from '../../hooks/useDefinition';
import { BadgeDefinition } from './definition';

/**
 * A non-interactive badge for labeling or categorizing content.
 *
 * @public
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>((props, ref) => {
  const { ownProps, restProps, dataAttributes } = useDefinition(
    BadgeDefinition,
    props,
  );
  const { classes, children, icon } = ownProps;

  return (
    <span ref={ref} className={classes.root} {...dataAttributes} {...restProps}>
      {icon && <span className={classes.icon}>{icon}</span>}
      {children}
    </span>
  );
});
